import { IonPage } from "@ionic/react";
import { useParams } from "react-router-dom";

import { pronouns } from "../../domain/family";
import { todayStr } from "../../domain/date";
import { useLinked } from "../../lib/linked";
import type { LinkedPart } from "../../sync/linked";
import { useKaya } from "../../store/kayaStore";
import { setEatingProfile, setTests, setTrainingProfile } from "../../store/toggles";
import { TrainingQuestions } from "../TrainQuestions";
import { LinkedWaiting, familyPath, useMyName } from "./linkedPage";

/**
 * Plan my exercise in a linked person's name (boards FamilyTrainingAnswers' Change, ADR-056): P3's
 * questionnaire with a subject — their state, no draft on this phone, and never the two safety
 * screens, which are theirs alone. Save stamps their exercise block with the same writer their
 * phone uses and "set by" this reader's name; food's shared answers go with it when this flow
 * asked them, and the counts typed on the tests step go as that day's tests block.
 */
export default function FamilyExercise() {
  const { id } = useParams<{ id: string }>();
  const q = useLinked(id);
  const showToast = useKaya((s) => s.showToast);
  const me = useMyName();
  const back = familyPath(id, "training");
  const l = q.linked;

  return (
    <IonPage>
      {l ? (
        <TrainingQuestions
          subject={{
            S: l.S,
            name: l.name,
            pron: pronouns(l.S),
            by: me,
            back,
            save: async (answers, extra) => {
              const parts: LinkedPart[] = ["training"];
              if (extra.eating) parts.push("eating");
              if (extra.tests) parts.push("tests");
              const overruled = await q.write(parts, (d) => {
                if (extra.eating) setEatingProfile(d, extra.eating);
                setTrainingProfile(d, { ...answers, setBy: me });
                if (extra.tests) setTests(d, todayStr(), { ...extra.tests, by: me });
              });
              const p = pronouns(l.S);
              showToast(
                overruled.length
                  ? `${l.name} changed these on ${p.their} phone since — ${p.their} answers were kept`
                  : `Saved to ${l.name}’s account`
              );
            },
          }}
        />
      ) : (
        <LinkedWaiting q={q} title="Exercise answers" back={{ to: back, label: "Back" }} />
      )}
    </IonPage>
  );
}
